/**
 * Initialisation Sentry du front standalone GGVie (copie adaptée de `sandbox/src/sentry.ts`,
 * isolation ADR-019 : aucun import `@/`).
 *
 * No-op total si `SENTRY_DSN` est vide (dev local, environnement sans projet Sentry).
 * DSN injecté via runtime-config (`window.__RUNTIME_CONFIG__`, vhost
 * `docker/sandbox/ggvie.conf.template`) — voir `@ggvie-consulting/config`.
 *
 * Écarts volontaires avec la sandbox :
 * - TOUT l'évènement est nettoyé des jetons portés par une URL (`/sso?token=…`,
 *   `/set-password/:token`, `/forgot-password/:token`) : erreurs, transactions, spans et
 *   fils d'Ariane (cf. `sentryScrub.ts`). Sentry démarre avant que `SsoView` ne retire le
 *   jeton SSO de la barre d'adresse ; la sandbox n'a pas de route `/sso` (ADR-044).
 * - `sendDefaultPii: false` : front servi aux conseillers du client, l'adresse IP n'est
 *   pas collectée.
 * - Une erreur d'initialisation est avalée : l'observabilité ne doit jamais empêcher
 *   l'application de démarrer chez le client.
 */

import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

import {
  SENTRY_DSN,
  SENTRY_ENVIRONMENT,
  SENTRY_RELEASE,
  SENTRY_TRACES_SAMPLE_RATE,
} from '@ggvie-consulting/config'
import { scrubDeep } from '@ggvie-consulting/sentryScrub'

export function initSentry(app: App, router: Router): void {
  if (!SENTRY_DSN) {
    return
  }

  try {
    Sentry.init({
      app,
      dsn: SENTRY_DSN,
      environment: SENTRY_ENVIRONMENT,
      release: SENTRY_RELEASE || undefined,
      tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
      sendDefaultPii: false,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
      ],
      beforeSend: (event) => scrubDeep(event),
      beforeSendTransaction: (event) => scrubDeep(event),
      beforeSendSpan: (span) => scrubDeep(span),
      beforeBreadcrumb: (breadcrumb) => scrubDeep(breadcrumb),
    })
  } catch (error) {
    console.warn('[ggvie] Sentry non initialisé', error)
  }
}
